import { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, TextField, Box, IconButton, Chip, Button, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import { formatDate } from '../../utils/DateUtils';
import SupplierBillingStatementService from '../../services/SupplierBillingStatementService';
import ViewSupplierBillingStatementModal, { hasReplenishmentPermission } from './ViewSupplierBillingStatementModal';
import SupplierPaymentModal from './SupplierPaymentModal';
import SupplierMovementsModal from './SupplierMovementsModal';
import dayjs from 'dayjs';
import TableRefreshButton from '../../components/TableRefreshButton/TableRefreshButton';
import { formatMoney } from '../../utils/currency';

const SupplierBillingStatementTable = () => {
  const { t } = useTranslation();

  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [movementsSupplier, setMovementsSupplier] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSupplier, setPaymentSupplier] = useState(null);
  const canManagePayments = hasReplenishmentPermission();
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const searchTimeoutRef = useRef(null);

  const fetchStatements = useCallback(async (page, pageSize, search) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const response = await SupplierBillingStatementService.getSupplierBillingStatements(
        page + 1,
        pageSize,
        null, // supplierId
        null  // status
      );
      
      // Filtrar por término de búsqueda si existe
      let filteredStatements = response.statements || [];
      if (cleanSearch) {
        const searchLower = cleanSearch.toLowerCase();
        filteredStatements = filteredStatements.filter(statement =>
          statement.supplierName?.toLowerCase().includes(searchLower) ||
          statement.invoice?.toLowerCase().includes(searchLower)
        );
      }
      
      setStatements(filteredStatements);
      setTotalRows(response.totalCount || filteredStatements.length);
    } catch (error) {
      console.error('Error fetching supplier billing statements:', error);
      setStatements([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatements(paginationModel.page, paginationModel.pageSize, searchTerm);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchStatements, paginationModel.page, paginationModel.pageSize]);

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    fetchStatements(newModel.page, newModel.pageSize, searchTerm);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchStatements(0, paginationModel.pageSize, newSearchTerm);
    }, 500);
  };

  const handleViewStatement = (statement) => {
    setSelectedStatement(statement);
    setIsViewModalOpen(true);
  };

  const handleStatementUpdated = () => {
    fetchStatements(paginationModel.page, paginationModel.pageSize, searchTerm);
  };

  const columns = [
    { 
      field: 'supplierName', 
      headerName: t('supplier') || 'Proveedor', 
      flex: 1.5 
    },
    { 
      field: 'replenishmentsCount', 
      headerName: t('replenishments_count') || 'Cant. Reposiciones', 
      type: 'number',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'status',
      headerName: t('status') || 'Estado',
      flex: 1.3,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={SupplierBillingStatementService.getStatusText(params.value, t)}
          color={SupplierBillingStatementService.getStatusColor(params.value)}
          size="small"
        />
      )
    },
    { 
      field: 'pendingAmount', 
      headerName: t('pending_amount') || 'Monto Pendiente', 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? formatMoney(params) : '—', 
    },
    {
      field: 'dueDate',
      headerName: t('due_date') || 'Fecha Vencimiento',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => params ? formatDate(dayjs(params)) : '—'
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      minWidth: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title="Ver detalle de pedidos">
            <IconButton sx={{ width: 40, height: 40, flexShrink: 0 }} onClick={() => handleViewStatement(params.row)}>
              {getIcon('eye')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver movimientos">
            <IconButton sx={{ width: 40, height: 40, flexShrink: 0 }} onClick={() => setMovementsSupplier(params.row)}>
              {getIcon('movements')}
            </IconButton>
          </Tooltip>
          {canManagePayments && (
            <Tooltip title="Registrar pago">
              <IconButton
                sx={{ width: 40, height: 40, flexShrink: 0 }}
                onClick={() => { setPaymentSupplier(params.row); setPaymentModalOpen(true); }}
              >
                {getIcon('money')}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search') || 'Buscar'}
            variant="outlined"
            size="small"
            sx={{ width: 300 }}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchStatements(paginationModel.page, paginationModel.pageSize, searchTerm)}
            />
            {canManagePayments && (
              <Button
                variant="outlined"
                startIcon={getIcon('money')}
                onClick={() => { setPaymentSupplier(null); setPaymentModalOpen(true); }}
              >
                Registrar pago
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              loading={loading}
              rows={statements}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              rowCount={totalRows}
              paginationMode="server"
              getRowId={(row) => row.id || row.supplierId}
            />
          </Box>
        </Box>
      </Paper>
      
      <ViewSupplierBillingStatementModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        statement={selectedStatement}
        onUpdated={handleStatementUpdated}
      />

      <SupplierMovementsModal
        open={Boolean(movementsSupplier)}
        onClose={() => setMovementsSupplier(null)}
        supplier={movementsSupplier}
      />

      <SupplierPaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSaved={handleStatementUpdated}
        initialSupplier={paymentSupplier}
      />
    </>
  );
};

export default SupplierBillingStatementTable;
